/**
 * InfinitSales - Theme Context
 * Manages dark/light theme across the application
 */
import React, { createContext, useContext, useState, useEffect } from 'react';

// Theme colors
export const themes = {
  light: {
    // Backgrounds
    bgPrimary: '#ffffff',
    bgSecondary: '#f0f2f5',
    bgTertiary: '#e9edef',
    bgChat: '#efeae2',
    
    // Text
    textPrimary: '#111b21',
    textSecondary: '#667781',
    textTertiary: '#8696a0',
    
    // Borders
    borderColor: '#e9edef',
    borderLight: '#f0f2f5',
    
    // Accent
    accentPrimary: '#00a884',
    accentSecondary: '#25d366',
    accentHover: '#008f72',
    
    // Messages
    messageSent: '#d9fdd3',
    messageReceived: '#ffffff',
    messageSentText: '#111b21',
    messageReceivedText: '#111b21',
    messageTimeSent: 'rgba(0, 0, 0, 0.45)',
    
    // Input
    inputBg: '#ffffff',
    inputBorder: '#e9edef',
    
    // Header/Sidebar
    headerBg: '#f0f2f5',
    sidebarBg: '#ffffff',
    
    // Status colors
    danger: '#ea4335',
    dangerHover: '#d33426',
    
    // Shadows
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.1)',
    shadowMd: '0 2px 8px rgba(0, 0, 0, 0.15)',
    shadowLg: '0 4px 16px rgba(0, 0, 0, 0.2)',
  },
  dark: {
    // Backgrounds
    bgPrimary: '#111b21',
    bgSecondary: '#202c33',
    bgTertiary: '#2a3942',
    bgChat: '#0b141a',
    
    // Text
    textPrimary: '#e9edef',
    textSecondary: '#8696a0',
    textTertiary: '#667781',
    
    // Borders
    borderColor: '#2a3942',
    borderLight: '#222d34',
    
    // Accent
    accentPrimary: '#00a884',
    accentSecondary: '#25d366',
    accentHover: '#00c49a',
    
    // Messages
    messageSent: '#005c4b',
    messageReceived: '#202c33',
    messageSentText: '#e9edef',
    messageReceivedText: '#e9edef',
    messageTimeSent: 'rgba(255, 255, 255, 0.6)',
    
    // Input
    inputBg: '#2a3942',
    inputBorder: '#3b4a54',
    
    // Header/Sidebar
    headerBg: '#202c33',
    sidebarBg: '#111b21',
    
    // Status colors
    danger: '#ea4335',
    dangerHover: '#d33426',
    
    // Shadows
    shadowSm: '0 1px 2px rgba(0, 0, 0, 0.3)',
    shadowMd: '0 2px 8px rgba(0, 0, 0, 0.4)',
    shadowLg: '0 4px 16px rgba(0, 0, 0, 0.5)',
  }
};

const ThemeContext = createContext();

export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    // Migration: check new key first, fall back to old key for existing users
    const saved = localStorage.getItem('infinitsales-theme') || localStorage.getItem('lumensales-theme');
    if (saved) return saved;
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  });

  useEffect(() => {
    localStorage.setItem('infinitsales-theme', theme);
    // Mantem a status bar do PWA (iOS 15+/Android) na cor do header do tema atual.
    // O SO escolhe o texto legivel (claro no tema escuro, escuro no tema claro),
    // evitando texto branco invisivel no modo claro.
    const meta = document.querySelector('meta[name="theme-color"]');
    if (meta) meta.setAttribute('content', themes[theme].bgPrimary);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'dark' ? 'light' : 'dark');
  };

  const colors = themes[theme];

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, colors }}>
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

export default ThemeContext;